import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import React from "react";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { LocalizationProvider } from "@mui/x-date-pickers";
import {
  MRT_ColumnOrderState,
  MRT_ToggleFiltersButton,
  MRT_ToggleGlobalFilterButton,
  MRT_Updater,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import ShowHideColumnsButton from "./ShowHideColumnsButton";
import { useErrorBoundary } from "react-error-boundary";
import { useNavigate } from "react-router";

import useProspectList from "../hooks/useProspectList";
import useProspectFilterParams from "../hooks/useProspectFilterParams";
import { submissionTimeField } from "./model";
import {
  defaultProspectPageSize,
  prospectTableInitialState,
} from "./tableConfig";
import { ProspectColumnMeta, prospectViewPath } from "./utils";

const getFilterPlaceholder = (
  columnId: string,
  header: string,
  rangeFilterIndex?: number,
) => {
  if (columnId === submissionTimeField) {
    if (rangeFilterIndex === 0) {
      return "From";
    }

    if (rangeFilterIndex === 1) {
      return "To";
    }
  }

  if (rangeFilterIndex === 0) {
    return "Min";
  }

  if (rangeFilterIndex === 1) {
    return "Max";
  }

  return `Filter by ${header}`;
};

const interactiveElements = "a, button, input, label";

const isFinishingTextSelection = () =>
  Boolean(window.getSelection()?.toString());

function ProspectList() {
  const { loading, hasResolved, rows, columns, error, fieldDefinitions } =
    useProspectList();
  const { showBoundary } = useErrorBoundary();
  const navigate = useNavigate();
  const {
    columnFilters,
    setColumnFilters,
    globalFilter,
    setGlobalFilter,
    resetFiltersAndColumns,
    columnVisibility,
    setColumnVisibility,
    columnOrder,
    setColumnOrder,
    showColumnFilters,
    setShowColumnFilters,
    showGlobalFilter,
    setShowGlobalFilter,
    sorting,
    setSorting,
    pagination,
    setPagination,
    hasSettled: viewStateHasSettled,
  } = useProspectFilterParams(fieldDefinitions);

  React.useEffect(() => {
    if (error) {
      showBoundary(error);
    }
  }, [error, showBoundary]);

  const handleColumnOrderChange = React.useCallback(
    (updater: MRT_Updater<MRT_ColumnOrderState>) => {
      setColumnOrder((currentColumnOrder) =>
        typeof updater === "function" ? updater(currentColumnOrder) : updater,
      );
    },
    [setColumnOrder],
  );

  const handleRowClick = (
    event: React.MouseEvent<HTMLTableRowElement>,
    prospectId: string,
  ) => {
    const target = event.target as Element;
    if (target.closest(interactiveElements) || isFinishingTextSelection()) {
      return;
    }

    const url = prospectViewPath(prospectId);
    if (event.metaKey || event.ctrlKey || event.button === 1) {
      window.open(url, "_blank", "noopener");
      return;
    }

    navigate(url);
  };

  const table = useMaterialReactTable({
    autoResetPageIndex: false,
    columns,
    data: rows,
    enableColumnActions: false,
    enableColumnFilterModes: false,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enableColumnPinning: true,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: true,
    enableMultiSort: false,
    enableSortingRemoval: false,
    enableStickyHeader: true,
    getColumnCanGlobalFilter: (column) => column.getIsVisible(),
    globalFilterFn: "contains",
    initialState: prospectTableInitialState,
    onColumnFiltersChange: setColumnFilters,
    onColumnOrderChange: handleColumnOrderChange,
    onColumnVisibilityChange: setColumnVisibility,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onShowColumnFiltersChange: setShowColumnFilters,
    onShowGlobalFilterChange: setShowGlobalFilter,
    onSortingChange: setSorting,
    renderToolbarInternalActions: ({ table }) => (
      <>
        <MRT_ToggleGlobalFilterButton table={table} />
        <MRT_ToggleFiltersButton table={table} />
        <ShowHideColumnsButton table={table} />
        <Tooltip title="Reset filters">
          <IconButton
            aria-label="Reset filters"
            onClick={resetFiltersAndColumns}
          >
            <RestartAltIcon />
          </IconButton>
        </Tooltip>
      </>
    ),
    layoutMode: "grid",
    muiFilterTextFieldProps: ({ column, rangeFilterIndex }) => {
      const meta = column.columnDef.meta as ProspectColumnMeta | undefined;
      const isNumberRange = meta?.filterVariant === "numberRange";
      return {
        placeholder: getFilterPlaceholder(
          column.id,
          String(column.columnDef.header),
          rangeFilterIndex,
        ),
        slotProps: isNumberRange
          ? { htmlInput: { inputMode: "decimal" as const } }
          : undefined,
        sx:
          column.id === submissionTimeField ? { minWidth: "140px" } : undefined,
      };
    },
    muiFilterDatePickerProps: {
      format: "dd/MM/yyyy",
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (event) => handleRowClick(event, row.original.id),
      onAuxClick: (event) => {
        if (event.button === 1) {
          handleRowClick(event, row.original.id);
        }
      },
      sx: { cursor: "pointer" },
    }),
    muiTableHeadProps: {
      sx: {
        top: "64px",
      },
    },
    muiTableHeadRowProps: {
      sx: {
        backgroundColor: "background.paper",
      },
    },
    muiPaginationProps: {
      rowsPerPageOptions: [defaultProspectPageSize],
      showRowsPerPage: false,
    },
    muiSearchTextFieldProps: {
      inputProps: {
        "aria-label": "Search prospects",
      },
      placeholder: "Search",
    },
    muiTopToolbarProps: {
      sx: {
        position: "sticky",
        left: 0,
        maxWidth: "100vw",
        px: 3,
        boxSizing: "border-box",
        "& > div:last-child": {
          position: "static",
          justifyContent: "flex-start",
        },
        "& > div:last-child > div": {
          justifyContent: "flex-start",
        },
      },
    },
    muiBottomToolbarProps: {
      sx: {
        position: "sticky",
        left: 0,
        maxWidth: "100vw",
        px: 3,
        boxSizing: "border-box",
      },
    },
    muiTableContainerProps: {
      sx: {
        maxWidth: "100%",
        width: "100%",
        maxHeight: "none",
        overflow: "visible",
      },
    },
    muiTablePaperProps: {
      sx: {
        overflow: "visible",
        width: "100%",
        maxWidth: "100%",
        display: "flex",
        flexDirection: "column",
      },
    },
    state: {
      columnFilters,
      columnOrder,
      columnVisibility,
      globalFilter,
      isLoading: loading || !hasResolved,
      pagination,
      showColumnFilters,
      showGlobalFilter,
      sorting,
    },
  });

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        data-testid="prospect-list-table-container"
        sx={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {viewStateHasSettled ? (
          <MaterialReactTable table={table} />
        ) : (
          <Box
            data-testid="prospect-list-hydrating"
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 240,
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Box>
    </LocalizationProvider>
  );
}

export default ProspectList;
